import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import DocTable from '../components/admin/DocTable';
import DocForm from '../components/admin/DocForm';
import SettingsForm from '../components/admin/SettingsForm';
import { useAuth } from '../context/AuthContext';
import { useDocs } from '../hooks/useDocs';
import { createDoc, deleteDoc, updateDoc } from '../api/docs';
import { getSettings } from '../api/settings';
import { DocPage } from '../types/doc';
import { Settings } from '../api/settings';

type Tab = 'docs' | 'settings';

const AdminDashboard = () => {
  const { admin, token, logout } = useAuth();
  const { data: docs, mutate } = useDocs();
  const [editing, setEditing] = useState<DocPage | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('docs');
  const [settings, setSettings] = useState<Settings | null>(null);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await getSettings();
        setSettings(data);
      } catch (error) {
        console.error('Failed to load settings:', error);
      }
    };
    loadSettings();
  }, []);

  if (!admin) {
    return null;
  }

  if (!token) {
    return null;
  }

  const handleSave = async (payload: Partial<DocPage>) => {
    if (!token) return;
    try {
      setIsSaving(true);
      if (editing?._id) {
        await updateDoc(editing._id, payload, token);
        toast.success('Documentation updated');
      } else {
        await createDoc(payload, token);
        toast.success('Documentation created');
      }
      await mutate();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Unable to save');
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (doc: DocPage) => {
    if (!token) return;
    if (!window.confirm(`Delete ${doc.title}?`)) return;
    try {
      await deleteDoc(doc._id, token);
      toast.success('Documentation deleted');
      await mutate();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Unable to delete');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 rounded-3xl border border-slate-200 bg-white/80 p-6 shadow-card dark:border-slate-800 dark:bg-slate-900/70 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.3em] text-brand-500">Admin</p>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Documentation control room</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Signed in as {admin.name} · {admin.email}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            className="rounded-2xl border border-rose-200 px-4 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-50 dark:border-rose-900/40 dark:text-rose-300 dark:hover:bg-rose-900/10"
            onClick={logout}
          >
            Sign out
          </button>
        </div>
      </div>

      <div className="flex gap-2 border-b border-slate-200 dark:border-slate-800">
        <button
          type="button"
          onClick={() => {
            setActiveTab('docs');
            setEditing(null);
          }}
          className={`px-4 py-2 text-sm font-semibold transition ${
            activeTab === 'docs'
              ? 'border-b-2 border-brand-500 text-brand-600 dark:text-brand-400'
              : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
          }`}
        >
          Documentation
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('settings')}
          className={`px-4 py-2 text-sm font-semibold transition ${
            activeTab === 'settings'
              ? 'border-b-2 border-brand-500 text-brand-600 dark:text-brand-400'
              : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
          }`}
        >
          Branding & Settings
        </button>
      </div>

      {activeTab === 'docs' && (
        <>
          <div className="flex justify-end">
            <button
              type="button"
              className="rounded-2xl bg-brand-500 px-6 py-2 text-sm font-semibold text-white shadow transition hover:bg-brand-600"
              onClick={() => {
                if (editing) {
                  setEditing(null);
                } else {
                  setEditing({} as DocPage);
                }
              }}
            >
              {editing ? 'Cancel' : '+ Create New Documentation'}
            </button>
          </div>
          <DocTable docs={docs} onEdit={(doc) => setEditing(doc)} onDelete={handleDelete} />
          {editing && (
            <DocForm
              initial={editing._id ? editing : undefined}
              loading={isSaving}
              onSubmit={async (payload) => {
                await handleSave(payload);
                setEditing(null);
              }}
            />
          )}
        </>
      )}

      {activeTab === 'settings' && (
        <SettingsForm
          initial={settings || undefined}
          onUpdate={async () => {
            const data = await getSettings();
            setSettings(data);
          }}
        />
      )}
    </div>
  );
};

export default AdminDashboard;

